import { Button, ButtonLevel } from '@vizit/atoms';
import { getMetricLabel } from '@vizit/constants';
import { RefreshCw04Icon } from '@vizit/icons';
import { colors } from '@vizit/theme';
import * as React from 'react';
import { BaseMetricCard } from '../base-metric-card';
import { BooleanBar } from '../metric-charts/boolean-bar';
import type { BooleanMetric } from '../metric-charts/types';

interface BooleanMetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The boolean metric data to display
   */
  metric: BooleanMetric;
  /**
   * Optional description text to display below the title
   */
  description?: string;
  /**
   * Callback when the reorder button is clicked
   */
  onReorder?: () => void;
  /**
   * Width of the chart
   * @default 300
   */
  chartWidth?: number;
  /**
   * Height of the chart
   * @default 20
   */
  chartHeight?: number;
  /**
   * Number of filled zaps for impact badge (0-4)
   */
  filledZapCount: number;
  /**
   * Whether the card is in loading state
   * @default false
   */
  isLoading?: boolean;
  /**
   * Whether the reorder mutation is in progress
   * @default false
   */
  isReordering?: boolean;
}

/**
 * BooleanMetricCard displays a boolean metric with chart, button, and impact badge
 */
const BooleanMetricCard = React.forwardRef<HTMLDivElement, BooleanMetricCardProps>((props, forwardedRef) => {
  const {
    metric,
    description,
    onReorder,
    chartWidth = 300,
    chartHeight = 20,
    filledZapCount,
    isLoading = false,
    isReordering = false,
    ...other
  } = props;

  const label = getMetricLabel(metric.key);

  return (
    <BaseMetricCard
      ref={forwardedRef}
      label={label}
      description={description}
      filledZapCount={filledZapCount}
      isLoading={isLoading}
      footer={
        <Button
          iconStart={<RefreshCw04Icon color={colors['gray-utility']['400']} size={20} />}
          onPress={onReorder}
          isDisabled={metric.currentValue || isReordering}
          level={ButtonLevel.Secondary}
          className="w-full font-semibold"
        >
          Reorder assets
        </Button>
      }
      {...other}
    >
      <BooleanBar metric={metric} width={chartWidth} height={chartHeight} />
    </BaseMetricCard>
  );
});

export type { BooleanMetricCardProps };
export { BooleanMetricCard };
